import {
  AIDescriptionResponse,
  APIAggregatesResponse,
  APIReportAttributes,
  APIReportColumnSuggestions,
  APIReportFilters,
  ApiReportViewStateResponse,
  APISearchAggregatesResponse,
  APISearchResponse,
  ArdoqId,
  GraphlakeSnapshotId,
  NonPersistedReport,
  Report,
  ReportColumn,
  ReportQuery,
  ReportSearchAggregatesArgs,
  ReportSearchArgs,
  ReportViewState,
  SortOrder,
  UpdateReport,
} from '@ardoq/api-types';
import { ApiResponse } from './types';
import {
  fetchAndRespondWith,
  fetchBlob,
  fetchEntities,
  fetchEntity,
  fetchJson,
  headers,
  Method,
  renameEntity,
  toRequestOptions,
} from './api';

type ExportParams = { filters?: APIReportFilters };

const exportReport = async (
  id: ArdoqId,
  params: ExportParams
): ApiResponse<Blob> => {
  const request = new Request(
    `/api/report/${id}/export`,
    toRequestOptions(Method.POST, params)
  );
  return fetchBlob(request);
};

const exportReportExcel = async (
  id: ArdoqId,
  params: ExportParams
): ApiResponse<Blob> => {
  const request = new Request(
    `/api/report/${id}/export-excel`,
    toRequestOptions(Method.POST, params)
  );
  return fetchBlob(request);
};

type Usage = {
  totalUseCount: number;
  dashboards: Array<{ _id: ArdoqId; name: string }>;
};

const usage = (reportIds: ArdoqId[]): ApiResponse<Usage> => {
  const request = new Request(
    `/api/report/usage`,
    toRequestOptions(Method.POST, { reportIds })
  );
  return fetchJson<Usage>(request);
};

const search = ({
  query,
  reportId,
}: {
  query: ReportSearchArgs;
  reportId?: string;
}): ApiResponse<APISearchResponse> => {
  // When creating new report, there is no report id.
  // When updating an existing report, there is a report id,
  // search by report id if exists, to get renamed report columns
  const url = new URL(
    reportId ? `/api/report/${reportId}/search` : `/api/report/search`,
    window.location.origin
  );
  const request = new Request(url, toRequestOptions(Method.POST, query));

  return fetchJson<APISearchResponse>(request);
};

const searchAggregates = ({
  query,
  reportId,
}: {
  query: ReportSearchAggregatesArgs;
  reportId?: string;
}): ApiResponse<APISearchAggregatesResponse> => {
  // When creating new report, there is no report id.
  // When updating an existing report, there is a report id,
  // search by report id if exists
  const url = new URL(
    reportId
      ? `/api/report/${reportId}/search/aggregates`
      : `/api/report/search/aggregates`,
    window.location.origin
  );

  const request = new Request(url, toRequestOptions(Method.POST, query));

  return fetchJson<APISearchAggregatesResponse>(request);
};

const create = (
  report: NonPersistedReport
): ApiResponse<APIReportAttributes> => {
  const request = new Request(
    '/api/report/create-query-and-report',
    toRequestOptions(Method.POST, report)
  );
  return fetchJson<APIReportAttributes>(request);
};

const update = (
  report: UpdateReport,
  keepHistoricalData: boolean
): ApiResponse<APIReportAttributes> => {
  const url = new URL(
    `/api/report/${report._id}/update-query-and-report`,
    window.location.origin
  );
  url.searchParams.set('discardHistoricalData', String(!keepHistoricalData));
  const request = new Request(url, toRequestOptions(Method.PUT, report));
  return fetchJson<APIReportAttributes>(request);
};

const deleteReport = (id: ArdoqId): ApiResponse<ArdoqId> => {
  const request = new Request(
    `/api/report/${id}/delete-query-and-report`,
    toRequestOptions(Method.DELETE, null)
  );
  return fetchAndRespondWith(request, id);
};

const reportWithHistoricalData = (id: ArdoqId): ApiResponse<Report> => {
  const url = new URL(`/api/report/${id}`, window.location.origin);
  url.searchParams.set('includeHasHistoricalData', 'true');
  const request = new Request(url, { headers });
  return fetchJson<Report>(request);
};

type DrilldownParams = {
  from: number;
  size: number;
  sort?: string;
  order?: SortOrder;
  filters?: APIReportFilters;
  noCache?: boolean;
  snapshot?: GraphlakeSnapshotId;
};

const toDrilldownUrl = (
  id: ArdoqId,
  snapshot: GraphlakeSnapshotId | undefined
): string =>
  snapshot === undefined
    ? `/api/report/${id}/drilldown`
    : `/api/graphlake/report/${id}/drilldown?snapshot=${snapshot}`;

const drilldown = (
  id: ArdoqId,
  { from, size, sort, order, filters, noCache, snapshot }: DrilldownParams
): ApiResponse<APISearchResponse> => {
  const request = new Request(
    toDrilldownUrl(id, snapshot),
    toRequestOptions(Method.POST, {
      id,
      from,
      size,
      sort,
      order,
      filters,
      cacheControl: noCache ? 'no-cache' : undefined,
    })
  );
  return fetchJson<APISearchResponse>(request);
};

const getColumnSuggestions = (
  reportId: string,
  columnKey: string,
  query: string,
  limit: number = 100
): ApiResponse<APIReportColumnSuggestions> => {
  const request = new Request(
    `/api/report/${reportId}/column/${columnKey}/suggestion?q=${query}&limit=${limit}`,
    toRequestOptions(Method.GET, null)
  );
  return fetchJson<APIReportColumnSuggestions>(request);
};

const copy = (id: ArdoqId, name: string): ApiResponse<Report> => {
  const request = new Request(
    `/api/report/copy`,
    toRequestOptions(Method.POST, { id, name })
  );
  return fetchJson<APIReportAttributes>(request);
};

type AggregatesParams = {
  filters?: APIReportFilters;
  snapshot?: GraphlakeSnapshotId;
};

const fetchAggregates = (
  reportId: ArdoqId,
  { filters, snapshot }: AggregatesParams
): ApiResponse<APIAggregatesResponse> => {
  const url =
    snapshot === undefined
      ? `/api/report/${reportId}/aggregates`
      : `/api/graphlake/report/${reportId}/aggregates?snapshot=${snapshot}`;
  const request = new Request(url, toRequestOptions(Method.POST, { filters }));
  return fetchJson<APIAggregatesResponse>(request);
};

const generateDescription = async (body: {
  name: string;
  description: string;
  query: ReportQuery | null;
  columns: ReportColumn[];
}): ApiResponse<AIDescriptionResponse> => {
  const request = new Request(
    `/api/report/description/autocomplete`,
    toRequestOptions(Method.POST, body)
  );
  return fetchJson<AIDescriptionResponse>(request);
};

const fetchViewState = (
  reportId: ArdoqId,
  stateId: ArdoqId
): ApiResponse<ReportViewState> => {
  const request = new Request(
    `/api/entity-view-state/report/${reportId}/${stateId}`,
    toRequestOptions(Method.GET, null)
  );
  return fetchJson<ReportViewState>(request);
};

const generateViewState = (
  reportId: ArdoqId,
  filters?: APIReportFilters
): ApiResponse<ApiReportViewStateResponse> => {
  const request = new Request(
    `/api/entity-view-state/report/${reportId}`,
    toRequestOptions(Method.POST, { filters })
  );
  return fetchJson<ApiReportViewStateResponse>(request);
};

export const reportApi = {
  copy,
  create,
  delete: deleteReport,
  drilldown,
  exportReport,
  exportReportExcel,
  fetch: fetchEntity<APIReportAttributes>('report'),
  fetchAll: fetchEntities<APIReportAttributes>('report'),
  fetchViewState,
  getColumnSuggestions,
  rename: renameEntity('report'),
  reportWithHistoricalData,
  search,
  searchAggregates,
  fetchAggregates,
  update,
  usage,
  generateDescription,
  generateViewState,
};
